<script setup lang="ts">
import { computed } from 'vue';

const UNIT_MAP = {
  lb: 'pound',
  kg: 'kilogram',
  w: 'watt',
  mph: 'mile-per-hour',
  kmh: 'kilometer-per-hour',
  in: 'inch',
  cm: 'centimeter',
  mi: 'mile',
  km: 'kilometer',
  wh: 'watt-hour',
  min: 'minute',
} as const;

type Unit = keyof typeof UNIT_MAP;

type PropsBase = {
  decimal?: boolean;
  unit?: Unit;
  space?: boolean;
};

type Range = {
  min: string | number;
  max: string | number;
};

type Value = {
  value: string | number;
};

type Props = PropsBase & Partial<Range> & Partial<Value>;

const props = defineProps<Props>();

const CUSTOM_UNITS = {
  w: 'W',
  wh: 'Wh',
} as const;

function custom(unit?: Unit): string | undefined {
  if (unit && unit in CUSTOM_UNITS) {
    return CUSTOM_UNITS[unit as keyof typeof CUSTOM_UNITS];
  }

  return undefined;
}

function config(unit?: Unit): Intl.NumberFormatOptions {
  const common: Intl.NumberFormatOptions = {
    maximumFractionDigits: props.decimal ? 2 : 0,
    unitDisplay: 'short',
  };

  if (!unit || unit in CUSTOM_UNITS) {
    return common;
  }

  return {
    style: 'unit',
    unit: UNIT_MAP[unit],
    ...common,
  };
}

const format = computed(() => new Intl.NumberFormat('en-US', config(props.unit)));
const formatNoUnit = computed(() => new Intl.NumberFormat('en-US', config()));

const range = computed(() => props.min !== undefined && props.max !== undefined);

function toNumber(value?: string | number) {
  return typeof value === 'string' ? Number(value) : value;
}

const space = computed(() => (props.space === undefined ? true : props.space));

function f(formatter: Intl.NumberFormat, value?: string | number, custom?: string) {
  const n = toNumber(value);
  const s = n === undefined || Number.isNaN(n) ? '' : formatter.format(n);
  if (!space.value) {
    return custom ? `${s}${custom}` : s;
  }
  return custom ? `${s} ${custom}` : s;
}

const number = computed(() => f(format.value, props.value, custom(props.unit)));

const min = computed(() => f(formatNoUnit.value, props.min));
const max = computed(() => f(format.value, props.max, custom(props.unit)));
</script>

<template>
  <template v-if="range">{{ min }} – {{ max }}</template>
  <template v-else>{{ number }}</template>
</template>
